"use client"

import { useCallback, useEffect, useRef, useState, type RefObject } from "react"
import { acquireGridVideoSlot } from "@/lib/grid-video-slots"

// Decides when a grid card's video-file thumbnail is allowed to start loading: only once the
// card is near the screen, and only when one of the few shared load slots is free. Without
// this every video card on the page fetched at once (see lib/grid-video-slots.ts).
export function useGridVideoThumb(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  const [load, setLoad] = useState(false)
  const [inView, setInView] = useState(false)
  const releaseRef = useRef<(() => void) | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const done = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    releaseRef.current?.()
    releaseRef.current = null
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!enabled || !el) return
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin: "300px" })
    io.observe(el)
    // Much wider net: a card more than ~2 screens away gives its video back, so a very long
    // scroll doesn't leave hundreds of decoded <video> elements alive. It reloads (through the
    // same slot queue) when scrolled back near.
    const far = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          done()
          setLoad(false)
        }
      },
      { rootMargin: "2000px" },
    )
    far.observe(el)
    return () => {
      io.disconnect()
      far.disconnect()
    }
  }, [enabled, ref, done])

  useEffect(() => {
    if (!enabled || !inView || load) return
    let cancelled = false
    acquireGridVideoSlot().then((release) => {
      if (cancelled) {
        release()
        return
      }
      releaseRef.current = release
      // Never let a stuck request hold a slot forever.
      timerRef.current = setTimeout(done, 8000)
      setLoad(true)
    })
    return () => {
      cancelled = true
    }
  }, [enabled, inView, load, done])

  useEffect(() => done, [done])

  return { load, inView, done }
}
